/**
 * Parses a date string as a local date to avoid timezone issues.
 *
 * When a date-only string like "2024-02-12" is passed to `new Date()`,
 * it's interpreted as UTC midnight. For users in timezones behind UTC
 * (like US timezones), this causes the date to display as the previous day.
 *
 * This function detects date-only strings (YYYY-MM-DD) and parses them
 * as local dates instead of UTC.
 */
export function parseLocalDate(dateString: string): Date {
  // Check if it's a date-only string (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  // For datetime strings, use standard parsing
  return new Date(dateString);
}

/**
 * Formats a date string for display, handling timezone issues for date-only strings.
 */
export function formatDate(
  dateString: string,
  options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  }
): string {
  const date = parseLocalDate(dateString);
  return date.toLocaleDateString("en-US", options);
}
